import { Button } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ValidationErrors } from '../../../utils/validation';
import { DefaultFinancialLogMutationReasons } from '../../../../common/enums/defaultFinancialLogMutationReasons';
import { freeOfChargeModalValidated } from '../../../../common/validation/financialLogMutation';
import { translateKey } from '../../../translate';
import { emptyValidationErrors, validate } from '../../../utils/validation';
import { Dropdown, FormGroup, FormLabel, NullableInputText } from '../common';
import { Modal } from './Modal';

interface IFinancialLogMutationModalProps {
    handleNo: () => Promise<void>;
    handleYes: (reason: string) => Promise<void>;
    isOpen: boolean;
}

export function FinancialLogMutationModal(props: IFinancialLogMutationModalProps) {
    const [reason, setReason] = useState<DefaultFinancialLogMutationReasons | null>(null);
    const [explanation, setExplanation] = useState<string | null>(null);
    const [errors, setErrors] = useState<ValidationErrors>(emptyValidationErrors);

    useEffect(() => {
        setReason(null);
        setExplanation(null);
        setErrors(emptyValidationErrors);
    }, [props.isOpen]);

    return (
        <Modal
            actionButtons={[
                <Button
                    onClick={props.handleNo}
                    variant="outline"
                >
                    {translateKey('common.cancelling')}
                </Button>,
                <Button
                    isDisabled={isConfirmationButtonDisabled()}
                    onClick={async () => await handleSubmit()}
                >
                    {translateKey('common.saveButton')}
                </Button>,
            ]}
            isOpen={props.isOpen}
            onClose={props.handleNo}
            size="medium"
            title={translateKey('fundingLogMutation.modalTitle')}
        >
            <FormGroup>
                <FormLabel>{translateKey('fundingLogMutation.reasonNoFunding')}</FormLabel>
                <Dropdown
                    error={errors.get('reason')}
                    onChange={(value) => {
                        setExplanation(null);
                        setReason(value);
                    }}
                    placeholder={translateKey('fundingLogMutation.selectAReason')}
                    selectOptions={Object.values(DefaultFinancialLogMutationReasons).map((option) => {
                        return { value: option, text: translateKey(`fundingLogMutation.${option}`) };
                    })}
                    value={reason}
                />
            </FormGroup>
            {reason === DefaultFinancialLogMutationReasons.other && (
                <FormGroup>
                    <FormLabel>{translateKey('fundingLogMutation.explainTheReason')}</FormLabel>
                    <NullableInputText
                        autoFocus
                        error={errors.get('explanation')}
                        onChange={(value) => setExplanation(value)}
                        onDoValidate={onDoValidate}
                        placeholder={translateKey('fundingLogMutation.writeHere')}
                        value={explanation}
                    ></NullableInputText>
                </FormGroup>
            )}
        </Modal>
    );

    function isConfirmationButtonDisabled() {
        if (reason === DefaultFinancialLogMutationReasons.other) {
            return !explanation;
        } else {
            return !reason;
        }
    }

    async function handleSubmit() {
        onDoValidate();
        if (!errors.size) {
            await props.handleYes(explanation ?? reason!);
        }
    }

    function onDoValidate() {
        const validationResult = validate(freeOfChargeModalValidated, { reason, explanation });
        setErrors(validationResult.errors);
    }
}
